/* eslint-disable no-restricted-syntax, no-await-in-loop, no-continue, no-promise-executor-return, consistent-return, no-void, max-len, class-methods-use-this, no-underscore-dangle, no-undef, no-plusplus, prefer-destructuring, no-param-reassign, no-use-before-define, no-nested-ternary, max-classes-per-file, no-unused-vars */
// Ported from newdtrader (packages/core/src/_common/base/dfunded); kept in its
// original style so fixes can be carried across the two copies.
/**
 * Direct WebSocket to the DFunded executor ("gateway"). Port of fasttrader's
 * src/services/dfunded-gateway.ts — keep the two in step.
 *
 * A Deriv login talks to Deriv over one socket: buys go in; ticks, contract
 * updates and balance changes stream back. This gives a funded session the
 * same shape instead of REST + database polling. Every risk rule still runs on
 * the server; when the socket is down, callers fall back to the REST path.
 */

class GatewayError extends Error {
    constructor(code, message) {
        super(message || String(code).split('_').join(' '));
        this.code = code;
        // Only set when the server sent wording of its own (a Deriv rejection).
        this.serverMessage = message || undefined;
    }
}

class DFundedGateway {
    constructor(url, credentials, handlers) {
        this.url = url;
        this.credentials = credentials;
        this.handlers = handlers;
        this.socket = null;
        this.ready = false;
        this.closed = false;
        this.nextId = 1;
        this.pending = new Map();
        this.tickSymbols = new Set();
        // Streamed quotes by id: { onUpdate, pending } (pending until the first price).
        this.quotes = new Map();
        this.nextQuoteId = 1;
        this.reconnectDelay = 500;
        this.reconnectTimer = null;
        this.pingTimer = null;
        this.open();
    }

    get isReady() {
        return this.ready;
    }

    open() {
        if (this.closed) return;
        const credentials = this.credentials();
        if (!credentials) {
            this.scheduleReconnect();
            return;
        }
        let socket;
        try {
            socket = new WebSocket(this.url);
        } catch {
            this.scheduleReconnect();
            return;
        }
        this.socket = socket;
        socket.addEventListener('open', () => {
            socket.send(JSON.stringify({ t: 'auth', token: credentials.token, accountId: credentials.accountId }));
        });
        socket.addEventListener('message', event => this.onMessage(String(event.data)));
        socket.addEventListener('close', () => {
            if (this.socket !== socket) return;
            this.socket = null;
            this.setReady(false);
            this.failPending(new GatewayError('GATEWAY_DISCONNECTED'));
            this.scheduleReconnect();
        });
        socket.addEventListener('error', () => {
            /* close follows */
        });
    }

    setReady(ready) {
        if (this.ready === ready) return;
        this.ready = ready;
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = ready ? setInterval(() => this.sendRaw({ t: 'ping' }), 25000) : null;
        this.handlers.onReadyChange(ready);
    }

    scheduleReconnect() {
        if (this.closed || this.reconnectTimer) return;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.open();
        }, this.reconnectDelay);
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 15000);
    }

    failPending(error) {
        this.pending.forEach(pending => {
            clearTimeout(pending.timer);
            pending.reject(error);
        });
        this.pending.clear();
        this.endQuotes(error);
    }

    /** Quote streams do not survive the socket: waiting callers fail, streaming ones are told it ended. */
    endQuotes(error) {
        const quotes = [...this.quotes.values()];
        this.quotes.clear();
        quotes.forEach(entry => {
            if (entry.pending) {
                clearTimeout(entry.pending.timer);
                entry.pending.reject(error);
            } else {
                entry.onUpdate({ error: { code: error.code || 'GATEWAY_DISCONNECTED' } });
            }
        });
    }

    sendRaw(message) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
    }

    onMessage(raw) {
        let message;
        try {
            message = JSON.parse(raw);
        } catch {
            return;
        }
        switch (message.t) {
            case 'ready':
                this.reconnectDelay = 500;
                this.setReady(true);
                if (message.account) this.handlers.onAccount(message.account);
                // Streams do not survive a reconnect on the server side.
                this.tickSymbols.forEach(symbol => this.sendRaw({ t: 'ticks', symbol }));
                break;
            case 'order':
                this.handlers.onOrder(message.order);
                break;
            case 'account':
                this.handlers.onAccount(message.account);
                break;
            case 'tick':
                this.handlers.onTick(String(message.symbol), message.tick);
                break;
            case 'ticks':
                // The server refused this symbol's stream; the history poller carries on.
                if (message.error) this.tickSymbols.delete(String(message.symbol));
                break;
            case 'quote': {
                const entry = this.quotes.get(String(message.id));
                if (!entry) break;
                if (entry.pending) {
                    const { pending } = entry;
                    entry.pending = null;
                    clearTimeout(pending.timer);
                    if (message.error) {
                        this.quotes.delete(String(message.id));
                        pending.reject(new GatewayError(String(message.error.code), message.error.message));
                    } else {
                        pending.resolve(message.response);
                    }
                    break;
                }
                if (message.error) this.quotes.delete(String(message.id));
                entry.onUpdate(message.error ? { error: message.error } : message.response);
                break;
            }
            case 'buy':
            case 'sell':
            case 'market': {
                const pending = this.pending.get(Number(message.id));
                if (!pending) break;
                this.pending.delete(Number(message.id));
                clearTimeout(pending.timer);
                if (message.error) pending.reject(new GatewayError(String(message.error.code), message.error.message));
                else pending.resolve(message);
                break;
            }
            default:
                break;
        }
    }

    call(message, timeoutMs) {
        if (!this.ready || !this.socket) return Promise.reject(new GatewayError('GATEWAY_DISCONNECTED'));
        const id = this.nextId++;
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                if (this.pending.delete(id)) reject(new GatewayError('GATEWAY_TIMEOUT'));
            }, timeoutMs);
            this.pending.set(id, { resolve, reject, timer });
            this.sendRaw({ ...message, id });
        });
    }

    /** Re-send auth on the open socket (after a token refresh or account switch). */
    reauthenticate() {
        const credentials = this.credentials();
        if (credentials) this.sendRaw({ t: 'auth', token: credentials.token, accountId: credentials.accountId });
    }

    async buy(order) {
        // The account the caller means travels with the order, so a socket still
        // signed in to another account refuses instead of buying on the wrong one.
        const send = async () => {
            const credentials = this.credentials();
            return (await this.call({ t: 'buy', order, accountId: credentials && credentials.accountId }, 30000)).order;
        };
        try {
            return await send();
        } catch (error) {
            // ACCOUNT_MISMATCH is not retried: the order was meant for the
            // account this tab was trading, and must not be re-sent to the one
            // another tab has since signed in to.
            const retryable = error instanceof GatewayError && error.code === 'SESSION_EXPIRED';
            if (!retryable) throw error;
            // Same client_order_id on the retry, so it can never buy twice.
            if (!(await this.handlers.refreshToken())) throw error;
            this.reauthenticate();
            await new Promise(resolve => setTimeout(resolve, 400));
            return send();
        }
    }

    async sell(contractId) {
        const credentials = this.credentials();
        await this.call({ t: 'sell', contractId, accountId: credentials && credentials.accountId }, 15000);
    }

    async market(request) {
        return (await this.call({ t: 'market', request }, 15000)).response;
    }

    /**
     * A quote priced by the account and app this session's buys use, streamed.
     * Resolves with { first, stop }; later prices (or { error } when the stream
     * ends) go to onUpdate. A server that does not answer quickly is treated as
     * unavailable, so the caller can fall back to the public price.
     */
    streamQuote(request, onUpdate, firstTimeoutMs = 4000) {
        if (!this.ready || !this.socket) return Promise.reject(new GatewayError('GATEWAY_DISCONNECTED'));
        const id = `q${this.nextQuoteId++}`;
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                const entry = this.quotes.get(id);
                if (!entry || !entry.pending) return;
                this.quotes.delete(id);
                this.sendRaw({ t: 'forget_quote', id });
                reject(new GatewayError('GATEWAY_TIMEOUT'));
            }, firstTimeoutMs);
            this.quotes.set(id, { onUpdate, pending: { resolve, reject, timer } });
            this.sendRaw({ t: 'quote', id, request });
        }).then(first => ({
            first,
            stop: () => {
                if (this.quotes.delete(id)) this.sendRaw({ t: 'forget_quote', id });
            },
        }));
    }

    watchTicks(symbol) {
        if (this.tickSymbols.has(symbol)) return;
        this.tickSymbols.add(symbol);
        this.sendRaw({ t: 'ticks', symbol });
    }

    unwatchTicks(symbol) {
        if (!this.tickSymbols.delete(symbol)) return;
        this.sendRaw({ t: 'forget_ticks', symbol });
    }

    streamsTicksFor(symbol) {
        return this.ready && this.tickSymbols.has(symbol);
    }

    close() {
        this.closed = true;
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.setReady(false);
        this.failPending(new GatewayError('GATEWAY_CLOSED'));
        try {
            if (this.socket) this.socket.close();
        } catch {
            /* already closed */
        }
        this.socket = null;
    }
}

module.exports = { DFundedGateway, GatewayError };
